import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Checkbox,
  Button,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { updatePreneedProfileBasicDetails } from "../../../api/preneed-section/preneed-profile-mgmt.api";
import DatePicker from "react-datepicker";
import { TimeRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import "react-datepicker/dist/react-datepicker.css";
import InfoPopup from "../../../common/components/info-popup/info-popup";
import "./operating-hours-popup.scss";
interface OperatingHoursPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  initialData?: any;
}

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const OperatingHoursPopup: React.FC<OperatingHoursPopupProps> = ({
  open,
  onClose,
  onSave,
  initialData,
}) => {
  const initialSelectedDays: Record<string, boolean> = {
    Sunday: false,
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
  };

  const [selectedDays, setSelectedDays] = useState(initialSelectedDays);
  // const [fromTime, setFromTime] = useState<Date | null>(null);
  // const [toTime, setToTime] = useState<Date | null>(null);

  const [timeRange, setTimeRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const [infoPopup, setInfoPopup] = useState({
    open: false,
    title: "",
    note: "",
  });

  React.useEffect(() => {
    if (open) {
      if (initialData?.days) {
        const newSelected: Record<string, boolean> = {};
        DAYS.forEach((day) => {
          newSelected[day] = initialData.days[day.toLowerCase()] || false;
        });
        setSelectedDays(newSelected);
      } else {
        setSelectedDays(initialSelectedDays);
      }

      if (initialData?.openingTime) {
        const [fromStr, toStr] = initialData.openingTime.split(" to ");
        const parseTime = (timeStr: string) => {
          if (!timeStr) return null;
          const [time, modifier] = timeStr.split(" ");
          let [hours, minutes] = time.split(":").map(Number);
          if (modifier === "PM" && hours < 12) hours += 12;
          if (modifier === "AM" && hours === 12) hours = 0;
          const d = new Date();
          d.setHours(hours, minutes, 0, 0);
          return d;
        };
        setTimeRange([parseTime(fromStr), parseTime(toStr)]);
      } else {
        setTimeRange(null as any);
      }
    }
  }, [open, initialData]);

  const handleCheckboxChange = (day: string) => {
    setSelectedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const formatTime = (date: Date | null) =>
    date?.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  const preparePayload = () => {
    const daysPayload: Record<string, boolean> = {};
    const [from, to] = timeRange || [null, null];

    DAYS.forEach((day) => {
      daysPayload[day.toLowerCase()] = selectedDays[day];
    });

    return {
      days: daysPayload,
      openingTime: from && to ? `${formatTime(from)} to ${formatTime(to)}` : "",
    };
  };

  const handleSave = () => {
    const hasAnyDaySelected = Object.values(selectedDays).some(Boolean);

    const from = timeRange?.[0] ?? null;
    const to = timeRange?.[1] ?? null;
    const hasTimeRange = from && to;

    if (hasAnyDaySelected && !hasTimeRange) {
      setInfoPopup({
        open: true,
        title: "Selection Required",
        note: "Please select operating hours for the selected days",
      });
      return;
    }

    if (!hasAnyDaySelected && (from || to)) {
      setInfoPopup({
        open: true,
        title: "Selection Required",
        note: "Please select at least one day for the operating hours",
      });
      return;
    }

    if (hasTimeRange && from > to) {
      setInfoPopup({
        open: true,
        title: "Invalid Time Range",
        note: `"From" time must be before "To" time`,
      });
      return;
    }

    const payload = preparePayload();
    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      maxWidth={false}
      PaperProps={{
        className: "operating-hours-dialog__paper",
      }}
      disableEscapeKeyDown
    >
      <DialogContent className="operating-hours-dialog">
        {/* Header */}
        <Box className="operating-hours-dialog__header">
          <Typography variant="h6" className="operating-hours-dialog__title">
            Set Operating Hours
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box className="operating-hours-dialog__body">
          <Typography>Which days are you open?</Typography>

          {/* Days checkboxes */}
          <Box className="operating-hours-dialog__days">
            {DAYS.map((day) => (
              <FormControlLabel
                className="operating-hours-dialog__form-label"
                key={day}
                control={
                  <Checkbox
                    checked={selectedDays[day]}
                    onChange={() => handleCheckboxChange(day)}
                    className="operating-hours-dialog__checkbox"
                  />
                }
                label={day}
              />
            ))}
          </Box>

          {/* Single Time Range Picker */}
          <Typography className="operating-hours-dialog__open-hours">
            Open Hours:
          </Typography>

          <Box display="flex" alignItems="center">
            <TimeRangePicker
              value={timeRange as any}
              onChange={(value) =>
                setTimeRange(value as [Date | null, Date | null])
              }
              format="hh:mm aa"
              // cleanable={false}
              style={{ width: 260 }}
              placeholder="HH:MM AM - HH:MM AM"
              cleanable
              showMeridiem
              character=" - "
            />
          </Box>
        </Box>

        {/* Actions */}
        <Box className="operating-hours-dialog__action-buttons">
          <Button
            variant="outlined"
            className="operating-hours-dialog__cancel-btn"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="operating-hours-dialog__save-btn"
            onClick={handleSave}
          >
            Save Operating Hours
          </Button>
        </Box>
      </DialogContent>

      <InfoPopup
        open={infoPopup.open}
        title={infoPopup.title}
        note={infoPopup.note}
        onClose={() => setInfoPopup((prev) => ({ ...prev, open: false }))}
      />
    </Dialog>
  );
};

export default OperatingHoursPopup;
